import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FaTachometerAlt, FaImages, FaBox, FaTags,
  FaEnvelope, FaUsers, FaStar, FaCog, FaSignOutAlt,
  FaBars, FaTimes, FaExternalLinkAlt, FaUsersCog, FaChevronLeft, FaBriefcase,
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { inquiryAPI, adminAPI } from '../../utils/api';
import toast from 'react-hot-toast';

const NAV = [
  { path: '/admin/dashboard',    icon: FaTachometerAlt, label: 'Dashboard' },
  { path: '/admin/inquiries',    icon: FaEnvelope,      label: 'Inquiries', badge: true },
  { path: '/admin/products',     icon: FaBox,           label: 'Products' },
  //{ path: '/admin/offers',       icon: FaTags,          label: 'Offers' },
  { path: '/admin/sliders',      icon: FaImages,        label: 'Sliders' },
  { path: '/admin/testimonials', icon: FaStar,          label: 'Testimonials' },
  { path: '/admin/subscribers',  icon: FaUsers,         label: 'Subscribers' },
  { path: '/admin/users',        icon: FaUsersCog,      label: 'Users' },
  { path: '/admin/careers',      icon: FaBriefcase,     label: 'Careers' },
  { path: '/admin/team',         icon: FaUsersCog,      label: 'Our Team' },
  { path: '/admin/settings',     icon: FaCog,           label: 'Settings' },
];

const resolveLogoUrl = (url) => {
  if (!url || url.trim() === '') return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads/')) {
    const base = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : '';
    return `${base}${url}`;
  }
  return url;
};

export default function AdminLayout() {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed]       = useState(false);
  const [mobileOpen, setMobileOpen]     = useState(false);
  const [unreadCount, setUnreadCount]   = useState(0);
  const [logoSrc, setLogoSrc]           = useState(null);

  const W = collapsed ? 64 : 240;

  useEffect(() => {
    inquiryAPI.getStats().then(r => setUnreadCount(r.data.data?.new || 0)).catch(() => {});
    adminAPI.getSettings().then(r => {
      const url = resolveLogoUrl(r.data.data?.logo_url);
      if (url) setLogoSrc(url);
    }).catch(() => {});
  }, []);

  const handleLogout = async () => {
    try { await logout(); } catch (_) {}
    toast.success('Signed out');
    navigate('/admin/login');
  };

  const isActive = (path) => location.pathname.startsWith(path);
  const pageLabel = NAV.find(n => location.pathname.startsWith(n.path))?.label || 'Admin';

  const SidebarContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Logo / Brand */}
      <div style={{
        padding: collapsed ? '20px 0' : '20px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        display: 'flex', alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        minHeight: 64,
      }}>
        {!collapsed && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {logoSrc ? (
              <img
                src={logoSrc}
                alt="Softlogic Australia"
                className="admin-logo-img"
                style={{ height: 52, maxWidth: 180, objectFit: 'contain' }}
                onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
              />
            ) : null}
            <span style={{ display: logoSrc ? 'none' : 'block', fontFamily:'Space Grotesk, system-ui, sans-serif', fontWeight: 800, fontSize: '1.1rem', color: '#fff' }}>Softlogic</span>
            <div style={{ fontSize: '0.68rem', color: 'rgba(0,212,255,0.7)', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 600 }}>
              Admin Portal
            </div>
          </div>
        )}
        <button onClick={() => setCollapsed(c => !c)}
          className="d-none d-lg-flex"
          style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 6, width: 28, height: 28, color: 'rgba(255,255,255,0.6)', cursor: 'pointer', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s' }}>
          <FaChevronLeft style={{ fontSize: '0.65rem', transform: collapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </button>
      </div>

      {/* Nav items */}
      <nav style={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}>
        {NAV.map(({ path, icon: Icon, label, badge }) => {
          const active = isActive(path);
          return (
            <Link key={path} to={path} title={collapsed ? label : undefined}
              style={{
                display: 'flex', alignItems: 'center',
                gap: collapsed ? 0 : 10,
                justifyContent: collapsed ? 'center' : 'flex-start',
                padding: collapsed ? '0.7rem 0' : '0.6rem 1rem',
                margin: '1px 8px',
                borderRadius: 8,
                background: active ? '#0ea5e9' : 'transparent',
                color: active ? '#fff' : 'rgba(255,255,255,0.62)',
                fontFamily:'Space Grotesk, system-ui, sans-serif', fontWeight: active ? 600 : 400,
                fontSize: '0.875rem', textDecoration: 'none',
                transition: 'all 0.15s', position: 'relative',
                boxShadow: active ? '0 2px 8px rgba(14,165,233,0.35)' : 'none',
              }}
              onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#fff'; } }}
              onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.62)'; } }}>
              <Icon style={{ fontSize: '0.95rem', flexShrink: 0 }} />
              {!collapsed && <span>{label}</span>}
              {!collapsed && badge && unreadCount > 0 && (
                <span style={{ marginLeft: 'auto', background: '#ef4444', color: '#fff', fontSize: '0.68rem', fontWeight: 700, padding: '1px 6px', borderRadius: 100, minWidth: 18, textAlign: 'center' }}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
              {collapsed && badge && unreadCount > 0 && (
                <span style={{ position: 'absolute', top: 6, right: 8, width: 7, height: 7, background: '#ef4444', borderRadius: '50%' }} />
              )}
            </Link>
          );
        })}
      </nav>

      {/* User + Logout */}
      <div style={{ padding: '12px 8px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        {!collapsed && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 8px', marginBottom: 4 }}>
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#0ea5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.9rem', color: '#fff', flexShrink: 0 }}>
              {user?.full_name?.[0] || 'A'}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '0.83rem', fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.full_name || 'Admin'}</div>
              <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', textTransform: 'capitalize' }}>{user?.role || 'admin'}</div>
            </div>
          </div>
        )}
        <a href="/" target="_blank" rel="noreferrer"
          style={{ display: 'flex', alignItems: 'center', gap: collapsed ? 0 : 8, justifyContent: collapsed ? 'center' : 'flex-start', padding: '0.5rem', margin: '1px 0', borderRadius: 6, color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', transition: 'all 0.15s', textDecoration: 'none' }}
          onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; e.currentTarget.style.background = 'transparent'; }}>
          <FaExternalLinkAlt style={{ fontSize: '0.75rem' }} />
          {!collapsed && <span>View Site</span>}
        </a>
        <button onClick={handleLogout}
          style={{ display: 'flex', alignItems: 'center', gap: collapsed ? 0 : 8, justifyContent: collapsed ? 'center' : 'flex-start', width: '100%', padding: '0.5rem', borderRadius: 6, background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', cursor: 'pointer', transition: 'all 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.color = '#fca5a5'; e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; e.currentTarget.style.background = 'transparent'; }}>
          <FaSignOutAlt style={{ fontSize: '0.82rem' }} />
          {!collapsed && <span>Sign Out</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f1f5f9' }}>
      <style>{`
        .admin-logo-img {
          filter: brightness(0) invert(1);
          transition: opacity 0.2s ease;
        }
        .admin-logo-img:hover { opacity: 0.85; }

        @media (min-width: 992px) {
          .admin-main-area { margin-left: ${W}px !important; transition: margin-left 0.22s ease; }
        }
        @media (max-width: 991px) {
          .admin-main-area { margin-left: 0 !important; }
        }
        .admin-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .admin-table-wrap table { min-width: 600px; }
        @media (max-width: 767px) {
          .admin-page-header { flex-direction: column; align-items: flex-start !important; gap: 12px; }
          .admin-page-header h4 { font-size: 1.1rem !important; }
          .admin-stats-grid { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      {/* Desktop sidebar — fixed position */}
      <div className="d-none d-lg-flex flex-column"
        style={{ width: W, background: '#060d1a', position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 200, transition: 'width 0.22s ease', overflow: 'hidden', flexShrink: 0 }}>
        <SidebarContent />
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} onClick={() => setMobileOpen(false)} />
          <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 240, background: '#060d1a', zIndex: 1 }}>
            <SidebarContent />
          </div>
        </div>
      )}

      {/* Main area */}
      <div style={{ flex: 1, marginLeft: 0, transition: 'margin-left 0.22s ease', minWidth: 0, display: 'flex', flexDirection: 'column' }} className="admin-main-area">

        {/* Desktop layout */}
        <div className="d-none d-lg-flex flex-column" style={{ flex: 1 }}>

          {/* Top bar */}
          <header style={{ background: '#060d1a', borderBottom: '1px solid rgba(255,255,255,0.08)', height: 60, display: 'flex', alignItems: 'center', padding: '0 1.75rem', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 }}>
            <div style={{ fontFamily:'Space Grotesk, system-ui, sans-serif', fontWeight: 600, color: 'rgba(255,255,255,0.9)', fontSize: '1rem' }}>
              {pageLabel}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#0ea5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#fff', fontSize: '0.9rem' }}>
                {user?.full_name?.[0] || 'A'}
              </div>
              <div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#fff' }}>{user?.full_name}</div>
                <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', textTransform: 'capitalize' }}>{user?.role}</div>
              </div>
            </div>
          </header>

          {/* Page content — full width, no maxWidth cap */}
          <main style={{ flex: 1, background:'#f1f5f9', padding: '1.75rem', width: '100%' }}>
            <Outlet />
          </main>
        </div>

        {/* Mobile */}
        <div className="d-lg-none" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <header style={{ background: '#060d1a', height: 56, display: 'flex', alignItems: 'center', padding: '0 1rem', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 }}>
            {logoSrc ? (
              <img src={logoSrc} alt="Softlogic"
                style={{ height: 32, maxWidth: 130, objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
                onError={e => { e.target.style.display = 'none'; }}
              />
            ) : (
              <span style={{ fontFamily:'Space Grotesk, system-ui, sans-serif', fontWeight: 700, color: '#fff', fontSize: '1rem' }}>Softlogic Admin</span>
            )}
            <button onClick={() => setMobileOpen(m => !m)}
              style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 6, color: '#fff', padding: '0.4rem 0.6rem', cursor: 'pointer', fontSize: '1rem' }}>
              {mobileOpen ? <FaTimes /> : <FaBars />}
            </button>
          </header>
          <main style={{ flex: 1, background:'#f1f5f9', padding: '1.25rem' }}>
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
